import moment from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Period } from '@spike/model';
import { RootState } from 'store';
import { useMarketplace, useTimeZone } from 'hooks';

import Header from '../../components/Header';
import MonthlyBreakdownsTable from './MonthlyBreakdownTable';
import styles from './MonthlyBreakdown.module.scss';

import { getMonthlyBreakdownReport } from 'actions/insights/bookings/monthlyBreakdownReport/MonthlyBreakdownReportActions';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';
import MonthlyBreakdownLineChart from './MonthlyBreakdownLineChart';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';

interface MonthlyBreakdownProps {
    description?: string;
}

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

const MONTH_LABELS: Record<(typeof MONTH_KEYS)[number], string> = {
    jan: 'January',
    feb: 'February',
    mar: 'March',
    apr: 'April',
    may: 'May',
    jun: 'June',
    jul: 'July',
    aug: 'August',
    sep: 'September',
    oct: 'October',
    nov: 'November',
    dec: 'December'
};

const num = (v: any) => Number(v ?? 0);

export const MonthlyBreakdown: FunctionComponent<MonthlyBreakdownProps> = props => {
    const dispatch = useDispatch();
    const [currentTab, setCurrentTab] = useState('table');
    const { thisYearOption, lastYearOption, nextYearOption } = useDateRangeOptions();

    const marketplace = useMarketplace();
    const timeZone = useTimeZone();

    const { report, loading } = useSelector((s: RootState) => s.monthlyBreakdownReport);

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('year'),
            to: now.clone().endOf('year')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);
    const [selectedYear, setSelectedYear] = useState<number>(moment().tz(timeZone).year());

    const calendar = {
        optionSelected: String(selectedYear)
    };

    const dateRangeOptions = [thisYearOption, lastYearOption, nextYearOption];

    useEffect(() => {
        dispatch(getMonthlyBreakdownReport(selectedYear));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear]);

    const handleChangePeriod = (p: Period | null, allTime: boolean) => {
        if (allTime || !p) {
            const y = moment().tz(timeZone).year();
            setSelectedYear(y);
            setPeriod(defaultPeriod);
            dispatch(getMonthlyBreakdownReport(y));
            return;
        }

        const year = p.from.clone().tz(timeZone).year();

        setSelectedYear(year);
        setPeriod({
            from: p.from.clone().startOf('year'),
            to: p.from.clone().endOf('year')
        });

        dispatch(getMonthlyBreakdownReport(year));
    };

    const exportFile = useMemo(() => {
        const safeReport: any = report || {};

        const getMonthValues = (values: Record<string, number> | undefined): string[] =>
            MONTH_KEYS.map(k => String(num(values?.[k])));

        const getCountValues = (row: any): string[] => MONTH_KEYS.map(k => String(num(row?.values?.[k]?.value)));

        const rows = [
            { metric: 'Total Bookings', months: getMonthValues(safeReport.totalBookings) },
            { metric: 'Total Clients', months: getMonthValues(safeReport.totalClients) },
            { metric: 'Total Pets', months: getMonthValues(safeReport.totalPets) },

            { metric: 'Total Services', months: getMonthValues(safeReport.totalServices) },
            { metric: 'Total Add-Ons', months: getMonthValues(safeReport.totalAddons) },
            { metric: 'Total Products', months: getMonthValues(safeReport.totalProducts) },
            { metric: 'Total Custom Items', months: getMonthValues(safeReport.totalCustomItems) },

            { metric: 'Total Completed', months: getCountValues(safeReport.completed) },
            { metric: 'Total Canceled', months: getCountValues(safeReport.canceled) },
            { metric: 'Total No Shows', months: getCountValues(safeReport.noShow) },

            {
                metric: 'Net Sales',
                months: MONTH_KEYS.map(k => num(safeReport.netSales?.[k]).toFixed(2))
            }
        ];

        return {
            name: `Monthly Breakdown ${marketplace?.basics?.businessName ?? ''} - ${selectedYear}.csv`,
            dataInMemory: true,
            headers: ['Metric', ...MONTH_KEYS.map(k => MONTH_LABELS[k])],
            dataToExport: rows.map(r => [r.metric, ...r.months]) as string[][]
        };
    }, [report, marketplace, selectedYear]);

    return (
        <div className={styles.container}>
            <div>
                <span className={styles.description}>{props.description}</span>
            </div>

            <div className={styles.categoryContainer}>
                <ToggleTabs
                    options={options}
                    value={currentTab}
                    setValue={setCurrentTab}
                />
            </div>

            <div>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                    dateOptions={dateRangeOptions}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <MonthlyBreakdownsTable
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <MonthlyBreakdownLineChart
                        loading={loading}
                        report={report}
                    />
                </div>
            )}
        </div>
    );
};

export default MonthlyBreakdown;
